/**
 * /zh-tw/comparisons/notion-ai — Draftlize vs Notion (Traditional Chinese).
 * Links to /zh-tw/decision-log.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiZhTw() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['zhtw']}
      documentTitle="做產品工作的 Notion 替代方案｜Draftlize vs Notion · Draftlize"
      documentDescription="Notion 儲存頁面；Draftlize 是 AI agent 能讀能寫的卡片圖，讓決策保持最新。並排比較兩者。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">比較 · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 60px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            Draftlize vs Notion——<br />
            <em>產品工作該選哪個？</em>
          </h1>
          <p className="lede">
            先講結論：Notion 是把東西寫下來的最佳工具。Draftlize 是一個基座，讓 AI agent 能把產品思考當成結構化卡片來<em>讀、也能寫</em>。兩者解的是相鄰的問題——與其說互相取代，不如說先弄清楚你現在需要哪一個。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">重點</span>
            <h2 className="serif">並排比較，<em>7 個面向。</em></h2>
          </div>
          <p>用同一份路線圖、同一組情境，在兩個工具各跑一遍後整理。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>基座形態</td><td className="ours yes">有型別的卡片圖</td><td className="partial">區塊組成的頁面</td></tr>
            <tr><td>AI 每次把決策當脈絡讀取</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>AI 以結構化卡片寫回</td><td className="ours yes">原生</td><td className="partial">能寫區塊但無結構</td></tr>
            <tr><td>依賴改變時 stale 傳播</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>自由格式文件・資料庫</td><td className="no">—</td><td className="yes">優秀</td></tr>
            <tr><td>團隊 wiki・權限・協作</td><td className="no">個人優先（v1）</td><td className="yes">優秀</td></tr>
            <tr><td>Claude Code / MCP 整合</td><td className="ours yes">原生</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>Notion 勝出</em>的地方。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">你需要 wiki</h3>
            <p>Notion 是現存最好的 wiki 工具。如果你的問題是「團隊的操作手冊放哪」，選 Notion。Draftlize 不打算做 wiki。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">你活在表格與資料庫裡</h3>
            <p>Notion 的資料庫／檢視系統無可匹敵。路線圖追蹤、衝刺規劃、評估表——這些形狀，Notion 的結構化集合每次都贏。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的團隊人數多</h3>
            <p>即時協作、細緻權限、留言、@提及——Notion 就是為此而生。Draftlize 的設計是個人優先。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize 勝出</em>的地方。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">真的「記得住」的 AI</h3>
            <p>Notion AI 的記憶，是盡力把內容檢索進對話。Draftlize 裡，agent 工作的基座本身就是卡片，所以它每次自動讀到你的定價決策卡。重複解釋歸零。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">spec drift 變得看得見</h3>
            <p>改 Notion 頁面，沒人會注意。改 Draftlize 的決策卡，所有依賴它的卡片自動標 <code>stale</code>。基座像建置系統追蹤失效那樣追蹤變更。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">給 Claude Code / Cursor / MCP 的一等脈絡</h3>
            <p>Notion AI 只活在 Notion 裡。Draftlize 的卡片經 CLI 落進你的 repo，透過 MCP 讓各家 AI 工具引用。決策從任何工具都指得出來。延伸閱讀 <a href="/zh-tw/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>決策紀錄</a>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion 回答「團隊把東西寫在哪」。Draftlize 回答「當 AI 做了一半的工作，產品思考在哪裡累積」。
        </blockquote>
        <cite>如果你只需要前者，Notion 就夠了。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">免費開始，<em>送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Notion 可以繼續用。Draftlize 是鋪在「AI 參與的那部分」底下的基座。新註冊送 $5、用多少付多少、餘額永不過期。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
